import { createApp } from 'vue'
import { createPinia } from "pinia"
import vuetify from "@/plugins/vuetify"
import {loadLayouts} from "@/layouts"
import {usePlugins} from "@/plugins"
import App from "./App.vue"
import router, { isNavigating } from "@/router"
import * as Sentry from "@sentry/vue"
import { App as CapacitorApp } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { Browser } from '@capacitor/browser'

const app = createApp(App)

app.use(router)
app.use(vuetify)
app.use(createPinia())

Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  // Don't attach IP address, cookies, or other request-level PII to events.
  sendDefaultPii: false,
  environment: import.meta.env.VITE_APP_ENV,  // local, staging, development, production
  integrations: [
    Sentry.replayIntegration({
      // Fully mask session replays so no personal data is recorded.
      maskAllText: true,
      maskAllInputs: true,
      blockAllMedia: true
    }),
    Sentry.browserTracingIntegration({ router }),
    Sentry.captureConsoleIntegration(),
    Sentry.feedbackIntegration({
      colorScheme: "system",
      id: "sentry-feedback"
    })
  ],
  // Session Replay
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
})
usePlugins(app)
loadLayouts(app)

router.isReady().then(async () => {
  // On Android, if the app cold-starts after an OAuth flow (Chrome Custom Tab closed),
  // navigate to settings instead of going through the PIN/account-select flow.
  if (localStorage.getItem('oauth_in_progress')) {
    localStorage.removeItem('oauth_in_progress')
    await router.replace('/settings#calendars')
  }
  app.mount('#app')
})

// Provide the loading state globally
app.provide('isNavigating', isNavigating)

// Flag to prevent browserFinished from firing after appUrlOpen already handled the return
let appUrlOpenHandled = false

// When the in-app browser closes (Android: user returns from Chrome Custom Tab)
// On Android, appUrlOpen doesn't fire from Chrome Custom Tabs, so this is the fallback.
if (Capacitor.getPlatform() === 'android') {
  Browser.addListener('browserFinished', () => {
    // Small delay to let any appUrlOpen fire first
    setTimeout(async () => {
      if (appUrlOpenHandled) {
        appUrlOpenHandled = false
        return
      }
      const params = new URLSearchParams(window.location.search)
      const hasOAuthReturn = params.has('customize_calendar_id') || params.has('google_success')
      if (!hasOAuthReturn) {
        localStorage.removeItem('oauth_in_progress')
        await router.replace('/calendar')
        await router.replace('/settings#calendars')
      }
    }, 500)
  })
}

// when app version is opened with a URL
CapacitorApp.addListener('appUrlOpen', async ({ url }) => {
  if (!url) return

  appUrlOpenHandled = true
  localStorage.removeItem('oauth_in_progress')

  // remove protocol
  const hostPath = url.replace(/^https?:\/\//, '')

  // get path (everything after the host)
  const path = '/' + hostPath.split('/').slice(1).join('/')

  // Skip API callback URLs — let the backend handle them via the in-app browser
  if (path.startsWith('/api/')) {
    return
  }

  // close the in-app browser if it's open
  try {
    await Browser.close()
  } catch (e) {
    // Browser.close() not needed
  }

  // Wait for the router to finish its initial navigation before navigating.
  // On cold-start, appUrlOpen fires while the initial route (/) is still resolving
  // through middleware. If we navigate now, the initial route's NotAuthorized redirect
  // to /account-select will override our navigation.
  await router.isReady()

  // If already on the same page, bounce through another route to force Vue to
  // unmount/remount the component so it re-fetches data (e.g. new calendar after OAuth)
  const targetPath = path.split('?')[0].split('#')[0]
  if (router.currentRoute.value.path === targetPath) {
    await router.replace('/calendar')
  }
  await router.replace(path)
})

export default app
